import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Typography,
  Collapse
} from '@material-ui/core';
import { useCallback, useMemo } from 'react';

import Alert from './Alert';
import DeleteIcon from '@material-ui/icons/Delete';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import moment from 'moment';
import ScannerOutlinedIcon from '@material-ui/icons/ScannerOutlined';
import BorderColorIcon from '@material-ui/icons/BorderColor';
import useTranslation from 'next-translate/useTranslation';

const DocumentItem = ({ document, onEdit, onDelete, disabled, isSelected, children }) => {
  const { t } = useTranslation('common');

  const handleEditClick = useCallback(() => {
    onEdit(document);
  }, [onEdit, document]);

  const handleDeleteClick = useCallback(() => {
    onDelete(document);
  }, [onDelete, document]);

  const expiryMoment = useMemo(() => {
    return document.expiryDate ? moment(document.expiryDate) : null;
  }, [document.expiryDate]);

  const isExpired = useMemo(() => {
    return expiryMoment ? moment().isSameOrAfter(expiryMoment) : false;
  }, [expiryMoment]);

  return (
    <>
      <ListItem button divider onClick={handleEditClick} selected={isSelected}>
        <ListItemText
          id={document._id}
          primary={
            <Box display="flex" flexDirection="column" justifyContent="center">
              <Box display="flex">
                <Box mr={1}>
                  {document._id === 'ttd' ? (
                    <BorderColorIcon fontSize='small' color="action" />
                  ) : document.type === 'text' ? (
                    <DescriptionOutlinedIcon color="action" />
                  ) : (
                    <ScannerOutlinedIcon color="action" />
                  )}
                </Box>
                <Typography component="div">{t(document.name)}</Typography>
              </Box>

              {expiryMoment ? (
                <Alert
                  severity={isExpired ? 'warning' : 'info'}
                  title={
                    isExpired
                      ? t('expired document')
                      : t('expiry {{relativeDate}}', {
                        relativeDate: expiryMoment.fromNow(),
                      })
                  }
                />
              ) : (
                !!document.description && (
                  <Typography variant="caption">
                    {document.description}
                  </Typography>
                )
              )}
            </Box>
          }
        />
        {!!onDelete ? (
          <ListItemSecondaryAction>
            <IconButton edge="end" onClick={handleDeleteClick} disabled={disabled}>
              <DeleteIcon />
            </IconButton>
          </ListItemSecondaryAction>
        ) : null}
      </ListItem>
      <Collapse in={isSelected} timeout="auto" unmountOnExit>
        {children}
      </Collapse>
    </>
  );
};

export default function DocumentList({
  documents,
  onEdit,
  onDelete,
  selectedId,
  renderPanel,
  disabled = false,
}) {
  return (
    <Paper variant="outlined">
      <Box minHeight={200}>
        <List>
          {documents
            ?.sort(({ type: type1 }, { type: type2 }) =>
              type1.localeCompare(type2)
            )
            .map((document) => (
              <DocumentItem
                key={document._id}
                document={document}
                onEdit={onEdit}
                onDelete={onDelete}
                isSelected={selectedId === document._id}
                disabled={disabled}
              >
                {selectedId === document._id ? renderPanel(document) : null}
              </DocumentItem>
            ))}
        </List>
      </Box>
    </Paper>
  );
}
